import { ExpandOutlined } from "@ant-design/icons";
import { Welcome } from "@ant-design/x";
import { BubbleDataType } from "@ant-design/x/es/bubble/BubbleList";
import { Button, Flex, Typography } from "antd";
import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { TbArrowsDiagonalMinimize2 } from "react-icons/tb";
import { useToken } from "../../hooks/useToken";
import { EMessageRole, TMessage } from "../../models/ai.model";
import { TProfile } from "../../models/users.model";
import { useAI } from "../../providers/AIProvider";
import { useTheme } from "../../providers/ThemeProvider";
import { useAuth } from "../../providers/UserProvider";
import { createMessage } from "../../services/ai.service";
import { TResponse } from "../../services/apiWorker";
import { getUserProfile } from "../../services/users.service";
import { AIDrawerMessagesRender } from "./AIDrawerMessagesRender";
import { AITextEditor } from "./AITextEditor";

export function AIDrawer({
  messageList,
  addMessage,
  updateMessage,

  preview = false,
}: {
  preview?: boolean;

  messageList: TResponse<TMessage>;
  addMessage: (data: TMessage) => void;
  updateMessage: (oldId: string, data: TMessage) => void;
}) {
  const { theme } = useTheme();

  const { token, screens } = useToken();
  const { user } = useAuth();
  const [profile, setProfile] = useState<TProfile>();
  const { session, isUsingAI, setUseAI, isExpandedMode, setExpandedMode } =
    useAI();

  useEffect(() => {
    getUserProfile({
      onSuccess: setProfile,
    });
  }, [user.uid]);

  console.log(theme);

  const canBeExpanded = isExpandedMode && screens.lg;

  const messages: BubbleDataType[] = [
    {
      key: "1923091",
      role: "greetings",
      content: (
        <Welcome
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "transparent",
            gap: token.padding,
          }}
          icon={
            <img
              src={theme == "DARK" ? "/icon-light.svg" : "/icon-dark.svg"}
              alt="Zeitfy Logo" // Adicione um texto alternativo para acessibilidade
              style={{ width: "64px", height: "auto" }} // Ajuste o tamanho conforme necessário
            />
          }
          title={
            <Typography
              style={{
                color: token.colorTextHeading,
                fontSize: token.fontSizeHeading4,
                fontWeight: token.fontWeightStrong,
                lineHeight: token.lineHeightHeading4,
                textAlign: "center",
              }}
            >
              Hi! {user?.displayName || profile?.name}
            </Typography>
          }
          description={
            <Typography
              style={{
                color: token.colorText,
                textAlign: "center",
                fontSize: token.fontSize,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: token.lineHeight,
              }}
            >
              Welcome to Zeitfy.
              <br />
              Every story matters—let’s start capturing yours.
            </Typography>
          }
        />
      ),
    },
    {
      key: 2,
      role: "suggestion",
      content: [
        { key: "6", description: "Write a summary about my childhood" },
        { key: "7", description: "Create a photo gallery of all my birthdays" },
        { key: "8", description: "Show me all my posts about music" },
      ],
    },
    ...messageList.content.map((message) => {
      return {
        key: message.id,
        role: message.role.toLowerCase(),
        content: message.content,
        typing:
          (message as any).typing === false ? false : { step: 2, interval: 20 },
      };
    }),
  ];

  if (!preview && !isUsingAI) {
    return null;
  }

  return (
    <Flex
      className="gradient-border-image"
      vertical
      justify="space-between"
      style={{
        position: preview ? "unset" : "fixed",
        top: screens.lg ? (isExpandedMode ? "190px" : "70px") : "100px",
        right: screens.lg ? "24px" : "10px",
        zIndex: 999,
        borderRadius: "10px",
        width: preview
          ? "100%"
          : screens.lg
          ? isExpandedMode
            ? "calc(100% - 48px)"
            : "500px"
          : "calc(100% - 20px)",
        height: preview
          ? "calc(100vh - 220px)"
          : screens.lg
          ? isExpandedMode
            ? "calc(100vh - 210px)"
            : "calc(100% - 120px)"
          : "calc(100% - 120px)",
        overflow: "hidden",
        background: "transparent",
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <Flex
        align="center"
        justify="flex-end"
        style={{
          padding: "10px 15px",
          borderBottom: `1px solid ${token.colorSplit}`,
          backgroundColor:
            theme === "LIGHT"
              ? "rgba(244, 244, 245, 0.80)"
              : "rgba(39, 39, 42, 0.20)",
          backdropFilter: "blur(20px)",
          width: "100%",
          borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0`,
          flexShrink: 0,
        }}
      >
        {screens.lg && !preview && (
          <Button
            type="text"
            title={isExpandedMode ? "Recolher" : "Expandir"}
            onClick={() => {
              setExpandedMode(!isExpandedMode);
            }}
            icon={
              isExpandedMode ? (
                <TbArrowsDiagonalMinimize2 size={20} color={token.colorIcon} />
              ) : (
                <ExpandOutlined
                  style={{ color: token.colorIcon, fontSize: "18px" }}
                />
              )
            }
          />
        )}

        {!preview && (
          <Button
            title="Fechar"
            type="text"
            onClick={() => {
              setUseAI(false);
            }}
            icon={<MdClose size={20} color={token.colorIcon} />}
          />
        )}
      </Flex>

      <Flex
        gap={screens.lg ? "24px" : "0px"}
        style={{
          flexGrow: 1,
          overflow: "hidden",
          padding: screens.lg ? "15px 24px" : "10px 15px",
          backgroundColor:
            theme === "LIGHT"
              ? "rgba(244, 244, 245, 0.80)"
              : "rgba(39, 39, 42, 0.80)",
          backdropFilter: canBeExpanded ? "blur(30px)" : "blur(20px)",
          borderRadius: `0 0 ${token.borderRadiusLG}px ${token.borderRadiusLG}px`,
        }}
      >
        {canBeExpanded && (
          <Flex
            vertical
            align="center"
            justify="space-between"
            style={{
              width: "50%",
              borderRight: "1px solid " + token.colorSplit,
              paddingRight: "24px",
              overflowY: "auto",
              height: "100%",
            }}
          >
            <Typography.Title
              level={4}
              style={{
                color: token.colorText,
                textAlign: "center",
                marginBottom: token.marginLG,
              }}
            >
              Preview Event
            </Typography.Title>
            <Flex vertical align="center" gap={token.marginMD}>
              <img
                style={{
                  borderRadius: "50%",
                  width: "200px",
                  height: "200px",
                  objectFit: "cover",
                  border: `2px solid ${token.colorPrimary}`,
                }}
                src={theme == "DARK" ? "/icon-light.svg" : "/icon-dark.svg"}
                alt="Preview event icon"
              />
              <Flex justify="center">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Button
                    key={index}
                    type="text"
                    disabled
                    icon={
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          backgroundColor:
                            index === 0 ? token.colorPrimary : token.colorIcon,
                          borderRadius: "50%",
                          opacity: index === 0 ? 1 : 0.5,
                        }}
                      />
                    }
                  />
                ))}
              </Flex>
            </Flex>
            <div style={{ flexGrow: 1 }} />
          </Flex>
        )}

        <Flex
          vertical
          style={{
            width: canBeExpanded ? "50%" : "100%",
            height: "100%",
          }}
        >
          <Flex
            vertical
            style={{
              flexGrow: 1,
              overflowY: "scroll",
              paddingBottom: token.paddingMD,
              height: "calc(100% - 60px)",
            }}
          >
            <AIDrawerMessagesRender messages={messages} />
          </Flex>
          <AITextEditor
            onAddMessage={(text) => {
              const messageId = new Date().getTime().toString();
              addMessage({
                id: messageId,
                content: { text },
                role: EMessageRole.USER,
                userId: user.uid,
                typing: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              } as any);
              return new Promise((resolve, reject) => {
                createMessage({
                  sessionId: session?.id || "",
                  content: { text },
                  onSuccess: (data) => {
                    resolve(data);
                    for (const message of data.content) {
                      if (message.role == EMessageRole.USER) {
                        updateMessage(messageId, {
                          ...message,
                          typing: false,
                        } as any);
                      } else {
                        if ((message as any).type == "FUNCTION") {
                          addMessage({
                            id: new Date().getTime().toString(),
                            content: (message as any).data,
                            role: EMessageRole.AI,
                            typing: false,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                          } as any);
                        } else {
                          addMessage(message);
                        }
                      }
                    }
                  },
                  onError: reject,
                });
              });
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
